import { setDocTitle } from "./web_ui";
import React from "react";
import { TextInputForm } from "./forms/text_input";

// pick a date util library
import * as luxon from "luxon";
import { DateForm } from "./forms/date_field";
import {
	SeshTextChannelData,
	PollCreationProps,
	IPollCreationParams,
	PollOption,
	Mentionable,
	DiscordAuthToken,
	GetPollDataRequest,
	IWebConfirmationData,
	PollDisplayData,
	PollType,
} from "../../../common/sesh_types";
import { Constants } from "../../../common/constants";
import SeshAccordian from "./forms/sesh_accordian";
import { TextAreaForm } from "./forms/text_area";
import FadeIn from "../../../src/vendored/FadeIn";
import { ColorPickerForm } from "./forms/color_picker";
import { PollOptionsList, createInitialPollOption } from "./forms/poll_options";
import { PollCreationConfirmation } from "./confirmation_page";
import { RoleRestrictionsForm } from "./forms/role_restrictions";
import { IDsToMentionables } from "./event_creation";
import { ChannelSelectForm } from "./forms/channel_dropdown";
import { DashboardUtils, FADE_IN_ANIMATION_DURATION } from "./dashboard_utils";
import { common_utils } from "../../../common/common_utils";
import { ImageUploaderForm } from "./forms/image_uploader";
import { RouteComponentProps, withRouter } from "react-router-dom";
import ColorPickerAccordian from "./forms/accordians/color_picker_accordian";
import ImageUploaderAccordian from "./forms/accordians/image_uploader_accordian";
import { MentionsFormFunction } from "./forms/mentions_form_function";
import { ChannelSelectFormWithTags } from "./forms/improved_components/channel_dropdown_with_tags";

export async function fetchPollData(
	auth_token: DiscordAuthToken,
	guild_id: string,
	message_id?: string
) {
	let request_body: GetPollDataRequest = {
		access_token: auth_token.access_token,
		token_type: auth_token.token_type,
		guild_id: guild_id,
		message_id: message_id,
	};

	return fetch(`/api/get_poll_data`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(request_body),
	}).then((response) =>
		response.json().then((value: PollCreationProps) => {
			if (value.err) {
				DashboardUtils.ShowDefaultErrorAlert(value.err);
				return;
			}

			return value;
		})
	);
}

interface PlaceholderFields {
	poll_title: string;
	description: string;
	end_time: string;
}

export interface PollCreationState {
	poll_title: string;
	description: string;
	options: PollOption[];
	end_time: luxon.DateTime;
	selected_channel: { id: string; forum_tag_ids: string[] };
	header_text: JSX.Element;
	placeholder_fields?: PlaceholderFields;
	user_timezone?: string;
	color?: string;
	allow_anyone_to_add: boolean;
	eligible_role_ids: string[];
	image_url: string;
	single_vote_per_user: boolean;
	is_anonymous: boolean;
	poll_type: PollType;

	user_mentions: string[];
	role_mentions: Mentionable[];

	loading_roles_and_channels?: boolean;
	is_poll_creation_success?: boolean;
	is_confirming: boolean;
}

export type PollCreationInputKeys = keyof PollCreationState;

export const PremiumFeatures: PollCreationInputKeys[] = ["eligible_role_ids"];

export const PlaceholderNames = [
	"Favorite animal?",
	"Favorite food?",
	"Most Sus Among Us Color?",
];

export const PlaceholderDescriptions = [
	"Every vote counts!",
	"Feel free to add more poll options.",
	"Vote early!",
];

export const PlaceholderTimes = [
	{
		start_time: "in 5 hours",
		duration: "1 hr 20 min",
	},
	{
		start_time: "Tomorrow at 5pm",
		duration: "1 hr 30 min",
	},
	{
		start_time: "8/21 at 2 pm",
		duration: "1 hr 40 min",
	},
];

class PollCreation extends React.Component<
	PollCreationProps & RouteComponentProps,
	PollCreationState
> {
	constructor(props) {
		super(props);

		this.state = this.createInitialState();

		this.handleSubmit = this.handleSubmit.bind(this);
		this.sendPollData = this.sendPollData.bind(this);
	}

	createInitialState(force_new: boolean = false): PollCreationState {
		let placeholder_name =
			PlaceholderNames[
				Math.floor(Math.random() * PlaceholderNames.length)
			];
		let placeholder_description =
			PlaceholderDescriptions[
				Math.floor(Math.random() * PlaceholderDescriptions.length)
			];
		let placeholder_time_fields =
			PlaceholderTimes[
				Math.floor(Math.random() * PlaceholderTimes.length)
			];

		let placeholder_fields: PlaceholderFields = {
			poll_title: placeholder_name,
			description: placeholder_description,
			end_time: placeholder_time_fields.start_time,
		};

		for (let key in placeholder_fields) {
			placeholder_fields[key] = "ex: " + placeholder_fields[key];
		}

		let user_timezone = this.props.user_timezone;
		if (!this.props.user_timezone) {
			user_timezone = luxon.DateTime.local().zone.name;
		}

		const poll_type = this.props.is_edit
			? this.props.edit_data.poll_type
			: PollType.Standard;

		let end_time;
		let header_text;
		let description = "";
		let poll_title = "";
		let options: PollOption[] = [];
		let image_url: string = "";
		let allow_anyone_to_add = this.props.guild_default_allow_anyone_to_add;
		let single_vote_per_user =
			this.props.guild_default_single_vote_per_user;
		let color = this.props.guild_default_poll_embed_color;
		let is_anonymous = false;
		if (!force_new && this.props.is_edit) {
			setDocTitle("Editing poll", this.props.guild_name);

			allow_anyone_to_add = this.props.edit_data.allow_anyone_to_add;
			single_vote_per_user = this.props.edit_data.single_vote_per_user;
			is_anonymous = this.props.edit_data.is_anonymous;
			color = this.props.edit_data.color;

			description = this.props.edit_data.description;
			poll_title = this.props.edit_data.poll_title;

			image_url = this.props.edit_data.image_url;

			header_text = this.getEditHeaderText(
				this.props.edit_data.poll_title
			);

			if (this.props.edit_data.end_time) {
				end_time = luxon.DateTime.fromJSDate(
					new Date(this.props.edit_data.end_time)
				).toUTC();
			}

			for (let name of this.props.edit_data.options) {
				options.push({
					name: name,
					is_newly_added: false,
				});
			}
		} else {
			setDocTitle("Creating poll", this.props.guild_name);
			header_text = this.getCreateHeaderText();
			options.push(createInitialPollOption(poll_type));
			options.push(createInitialPollOption(poll_type));
		}

		// const state = common_utils.createPollURLState(
		// 	this.props.auth_token.bot_client_id,
		// 	this.props.guild_data.id,
		// 	this.props.selected_channel.id,
		// 	this.props.selected_channel.name,
		// 	this.props.poll_message_id
		// );

		// history.replaceState(null, "", `/dashboard/#state=${state}`);

		const roles_and_channels_data = this.getUpdatedRolesAndChannelsData();

		if (!this.props.is_edit && roles_and_channels_data?.selected_channel) {
			this.props.history.replace(
				`create?channel_id=${roles_and_channels_data?.selected_channel.id}`
			);
		}

		return {
			poll_title,
			description,
			options,
			end_time,
			header_text,
			selected_channel: roles_and_channels_data?.selected_channel
				? {
						id: roles_and_channels_data?.selected_channel.id,
						forum_tag_ids: [],
				  }
				: undefined,
			placeholder_fields,
			user_timezone,
			eligible_role_ids: this.props.edit_data?.eligible_role_ids ?? [],
			image_url,
			allow_anyone_to_add,
			single_vote_per_user,
			is_anonymous,
			color,
			poll_type,
			role_mentions: roles_and_channels_data.create_role_mentions,
			user_mentions: this.props.edit_data?.user_mention_ids ?? [],
			loading_roles_and_channels: !Boolean(this.props.roles),
			is_confirming: false,
			is_poll_creation_success: false,
		};
	}

	getUpdatedRolesAndChannelsData(): {
		selected_channel: SeshTextChannelData;
		eligible_roles: Mentionable[];
		create_role_mentions: Mentionable[];
	} {
		const roles = this.props.roles;

		let create_role_mentions: Mentionable[] = [];

		if (!roles) {
			return {
				selected_channel: null,
				eligible_roles: [],
				create_role_mentions,
			};
		}

		const all_channels = this.props.is_edit
			? this.props.all_channel_data
			: this.props.all_channel_data.filter((val) => val.has_write);

		let eligible_roles: Mentionable[] = [];
		let selected_channel_id: string;

		if (this.props.can_select_channel) {
			const query = new URLSearchParams(this.props.location.search);
			const channel_id_from_param = query.get("channel_id");

			selected_channel_id = Boolean(channel_id_from_param)
				? channel_id_from_param
				: this.props.poll_channel_id;
		} else {
			selected_channel_id = this.props.poll_channel_id;
		}

		if (this.props.is_edit) {
			selected_channel_id = this.props.edit_data.selected_channel_id;

			eligible_roles = this.props.is_premium
				? IDsToMentionables(
						this.props.edit_data.eligible_role_ids,
						roles
				  )
				: [];

			create_role_mentions = IDsToMentionables(
				this.props.edit_data.role_mention_ids,
				roles
			);
		}

		let selected_channel = all_channels.find(
			(val) => val.id === selected_channel_id
		);

		if (!selected_channel) {
			selected_channel = all_channels[0];
		}

		if (!selected_channel) {
			DashboardUtils.ShowDefaultErrorAlert(
				"No valid channels found. Please ensure both you and sesh have read/write/embed links permissions in atleast one channel in the server."
			);

			this.props.history.push(
				common_utils.getGuildOptionsURL(this.props.guild_id)
			);
			return;
		}

		if (!this.props.is_edit) {
			this.props.history.replace(
				`create?channel_id=${selected_channel.id}`
			);
		}

		return {
			selected_channel,
			eligible_roles,
			create_role_mentions,
		};
	}

	componentWillUnmount() {
		if (this.props.on_unmount) {
			this.props.on_unmount();
		}
	}

	componentDidMount() {
		if (this.props.is_edit) {
			if (this.props.edit_data.end_time) {
				let end_time = luxon.DateTime.fromJSDate(
					new Date(this.props.edit_data.end_time)
				);

				if (end_time < luxon.DateTime.utc()) {
					DashboardUtils.ShowDefaultErrorAlert(
						"This poll has already ended. Unable to edit."
					);
					this.props.history.replace(
						common_utils.getPollCreateURL(this.props.guild_id)
					);
					return;
				}
			}
		}
	}

	componentDidUpdate(
		prevProps: PollCreationProps,
		prevState: PollCreationState
	) {
		const roles_updated =
			prevProps.roles == null && this.props.roles != null;
		if (roles_updated) {
			const new_state = this.getUpdatedRolesAndChannelsData();
			this.setState({
				selected_channel: {
					id: new_state?.selected_channel.id,
					forum_tag_ids: [],
				},
				role_mentions: new_state.create_role_mentions,
				loading_roles_and_channels: false,
			});
		}

		if (!this.props.is_edit) {
			const channel_changed =
				prevState.selected_channel?.id !=
				this.state.selected_channel?.id;

			if (channel_changed) {
				if (this.state.selected_channel) {
					this.props.history.replace(
						`create?channel_id=${this.state.selected_channel.id}`
					);
				}
			}
		}
	}

	handleSubmit(event) {
		event.preventDefault();

		this.sendPollData();
	}

	bulkUpdateState(data: { key: PollCreationInputKeys; value: any }[]) {
		const new_state = {};
		for (let datum of data) {
			new_state[datum.key] = datum.value;
		}

		this.setState(new_state);
	}

	updateState<Key extends PollCreationInputKeys>(
		key: Key,
		value: PollCreationState[Key]
	) {
		const new_state = {
			[key]: value,
		} as Pick<PollCreationState, Key>;
		this.setState(new_state);
	}

	getEditHeaderText(poll_title) {
		return (
			<span>
				{DashboardUtils.GetPollIcon()}
				Editing <i>{poll_title}</i>
			</span>
		);
	}

	getCreateHeaderText() {
		return <span>{DashboardUtils.GetPollIcon()}Creating Poll</span>;
	}

	getButtonText(channel_name) {
		if (this.props.is_edit) {
			return `Edit Poll in #${channel_name}`;
		} else {
			return `Create Poll in #${channel_name}`;
		}
	}

	getSelectedChannel() {
		return this.props.all_channel_data?.find(
			(val) => val.id === this.state.selected_channel?.id
		);
	}

	render_channel_select() {
		const channel_select_disabled =
			!this.props.can_select_channel ||
			this.props.edit_data?.is_reaction_based ||
			this.state.is_confirming;

		let channel_info_addenum: JSX.Element;
		if (!this.props.can_select_channel) {
			channel_info_addenum = (
				<i>. Disabled due to sesh server settings.</i>
			);
		}

		const channel_select_info = (
			<p>
				The channel where the poll will get sent to
				{channel_info_addenum}
			</p>
		);

		const name = "Channel";

		if (this.state.loading_roles_and_channels) {
			return (
				<div key="selected_channel_load" className="field">
					{DashboardUtils.CreateHeaderAndInfoButton(
						name,
						channel_select_info
					)}
					{DashboardUtils.RenderLoadingSelect()}
				</div>
			);
		}

		let below_element: JSX.Element;
		if (
			this.props.is_edit &&
			this.state.selected_channel.id !==
				this.props.edit_data.selected_channel_id
		) {
			const original_channel_name =
				this.props.all_channel_data.find(
					(val) => val.id === this.props.edit_data.selected_channel_id
				)?.name ?? "unknown";

			below_element = (
				<div className="note mt-4">
					Poll will be moved from{" "}
					<span className="has-text-weight-bold">
						#{original_channel_name}
					</span>{" "}
					to{" "}
					<span className="has-text-weight-bold">
						#{this.getSelectedChannel().name}
					</span>
				</div>
			);
		}

		return (
			<ChannelSelectFormWithTags
				id="selected_channel"
				name="Channel"
				desired_channel_id={
					this.props.is_edit ? undefined : this.props.poll_channel_id
				}
				initial_value={this.getSelectedChannel()}
				channel_data={this.props.all_channel_data}
				info={channel_select_info}
				disabled={channel_select_disabled}
				is_event={false}
				below_element={below_element}
				updateFunc={(val) =>
					this.updateState("selected_channel", {
						id: val.id,
						forum_tag_ids: [],
					})
				}
				sesh_channel_id={this.props.poll_channel_id}
				include_threads={true}
				selected_forum_tag_ids={
					this.state.selected_channel.forum_tag_ids
				}
				setSelectedTagIds={(val) =>
					this.updateState("selected_channel", {
						id: this.state.selected_channel.id,
						forum_tag_ids: val,
					})
				}
			></ChannelSelectFormWithTags>
		);
	}

	render_accordians() {
		return (
			<form className="creation-form">
				<h1 className="title is-3 has-text-centered mt-6">
					{this.state.header_text}
				</h1>
				{this.props.is_edit && (
					<span
						className="rsvp-data-link-edit-link"
						onClick={() => {
							this.props.history.push(
								common_utils.getPollResultsURL(
									this.props.guild_data.id,
									this.props.edit_data?.poll_message_id
								)
							);
						}}
					>
						View Results
					</span>
				)}
				<div className="creation-primary-fields">
					<TextInputForm
						id="poll_title"
						name="Poll Title"
						placeholder={this.state.placeholder_fields.poll_title}
						initial_value={this.state.poll_title}
						updateFunc={(val) =>
							this.updateState("poll_title", val)
						}
						char_limit={
							Constants.EMBED_TITLE_LIMIT -
							Constants.POLL_TITLE_PREFIX.length
						}
						info="The title of the poll"
						disabled={this.state.is_confirming}
					></TextInputForm>
					{this.render_channel_select()}
					<PollOptionsList
						name={`Poll Options (${this.state.options.length} / ${Constants.MAX_POLL_OPTIONS})`}
						poll_type={this.state.poll_type}
						guild_timezone={this.props.guild_timezone}
						personal_timezone={this.state.user_timezone}
						use_24h_clock={this.props.use_24h_clock}
						initial_value={this.state.options}
						info="Poll options to vote on"
						disabled={this.state.is_confirming}
						is_edit={this.props.is_edit}
						updateFunc={(options, type) => {
							this.bulkUpdateState([
								{ key: "options", value: options },
								{ key: "poll_type", value: type },
							]);
						}}
					/>
				</div>
				<SeshAccordian
					initial_value={this.state.description}
					opened_initially={Boolean(this.state.description)}
					field_key="description"
					is_server_premium={this.props.is_premium}
					label="Description"
					info={"The description of the poll"}
					disabled={this.state.is_confirming}
					render_child={(field_key, initial_value) => {
						return (
							<TextAreaForm
								initial_value={initial_value}
								rows={10}
								placeholder={
									this.state.placeholder_fields.description
								}
								updateFunc={(val) =>
									this.updateState(field_key, val)
								}
								char_limit={Constants.DESCRIPTION_CHAR_LIMIT}
							></TextAreaForm>
						);
					}}
				></SeshAccordian>
				<SeshAccordian
					initial_value={this.state.allow_anyone_to_add}
					opened_initially={Boolean(this.state.allow_anyone_to_add)}
					label="Allow Anyone To Add Poll Option"
					field_key="allow_anyone_to_add"
					is_server_premium={this.props.is_premium}
					info={
						"Allows anyone to add extra poll options from Discord"
					}
					render_child={null}
					updateFunc={(key, value) => this.updateState(key, value)}
					disabled={this.state.is_confirming}
				></SeshAccordian>
				<SeshAccordian
					initial_value={{
						user_mentions: this.state.user_mentions,
						role_mentions: this.state.role_mentions,
					}}
					loading={false}
					opened_initially={
						(this.props.edit_data?.role_mention_ids &&
							this.props.edit_data.role_mention_ids.length > 0) ||
						(this.props.edit_data?.user_mention_ids &&
							this.props.edit_data.user_mention_ids.length > 0)
					}
					field_key={undefined}
					is_server_premium={this.props.is_premium}
					label="Mentions"
					info={"Roles/Users to ping when the poll is created"}
					onCheckboxUnclicked={() =>
						this.setState({
							role_mentions: [],
							user_mentions: [],
						})
					}
					disabled={this.state.is_confirming || this.props.is_edit}
					render_child={(field_key, initial_value) => {
						return (
							<MentionsFormFunction
								initial_value={initial_value}
								roles={this.props.roles}
								updateFunc={(val) => {
									this.setState({
										role_mentions: val.role_mentions,
										user_mentions: val.user_mentions,
									});
								}}
								disabled={
									this.state.is_confirming ||
									this.props.is_edit
								}
							></MentionsFormFunction>
						);
					}}
				></SeshAccordian>
				<SeshAccordian
					initial_value={this.state.single_vote_per_user}
					opened_initially={this.state.single_vote_per_user}
					label="Single Vote Per User"
					field_key="single_vote_per_user"
					is_server_premium={this.props.is_premium}
					info={`Allow only one vote per user. If this is unchecked, then users can vote for multiple options. ${
						this.props.is_edit
							? "\nNOTE: Can not change on edit."
							: ""
					}`}
					render_child={null}
					updateFunc={(key, value) => this.updateState(key, value)}
					disabled={this.props.is_edit || this.state.is_confirming}
				/>
				<SeshAccordian
					initial_value={this.state.is_anonymous}
					opened_initially={this.state.is_anonymous}
					label="Anonymous"
					field_key="is_anonymous"
					is_server_premium={this.props.is_premium}
					info={`Prevent the names of poll voters from being seen by everyone, including admins.${
						this.props.is_edit
							? "\nNOTE: Can not modify this setting on an already created poll."
							: ""
					}`}
					render_child={
						!this.props.is_edit &&
						this.props.are_polls_default_reaction_based
							? () => (
									<div className="note">
										Enabling this setting will create a
										button based poll, instead of a reaction
										based one.
									</div>
							  )
							: null
					}
					updateFunc={(key, value) => this.updateState(key, value)}
					disabled={this.state.is_confirming || this.props.is_edit}
				/>
				<SeshAccordian
					initial_value={this.state.end_time}
					opened_initially={Boolean(this.state.end_time)}
					disabled={this.state.is_confirming}
					label="End Time"
					field_key="end_time"
					is_server_premium={this.props.is_premium}
					info={"The end time of the poll"}
					render_child={(field_key, initial_value) => {
						return (
							<DateForm
								placeholder={
									this.state.placeholder_fields.end_time
								}
								guild_zone={this.props.guild_timezone}
								personal_zone={this.state.user_timezone}
								initial_value={this.state.end_time}
								use_24h_clock={this.props.use_24h_clock}
								display_text={"Poll end time"}
								updateFunc={(val) =>
									this.updateState(field_key, val)
								}
							></DateForm>
						);
					}}
				></SeshAccordian>
				<ImageUploaderAccordian
					field_key={"image_url"}
					image_url={this.state.image_url}
					is_premium={this.props.is_premium}
					info={"The image for this poll"}
					updateFunc={(val) => this.updateState("image_url", val)}
					disabled={this.state.is_confirming}
				/>
				<ColorPickerAccordian
					field_key={"color"}
					label={"Color"}
					is_premium={this.props.is_premium}
					color={this.state.color}
					info="The color of the embed for this event"
					updateFunc={(val) => this.updateState("color", val)}
					disabled={this.state.is_confirming}
				/>
				<SeshAccordian
					initial_value={this.state.eligible_role_ids}
					opened_initially={Boolean(
						this.props.edit_data?.eligible_role_ids?.length > 0
					)}
					disabled={this.state.is_confirming}
					field_key="eligible_role_ids"
					is_server_premium={this.props.is_premium}
					label="Role Restrictions"
					loading={this.state.loading_roles_and_channels}
					info={"Limit poll voting to only certain roles"}
					render_child={(field_key, initial_value) => {
						return (
							<RoleRestrictionsForm
								role_ids={initial_value}
								updateFunc={(val) => {
									this.updateState(field_key, val);
								}}
							></RoleRestrictionsForm>
						);
					}}
				></SeshAccordian>
				<div className="field submit-button">
					<DashboardUtils.CreateFormConfirmButton
						blocked_by_load={this.state.loading_roles_and_channels}
						channel_name={this.getSelectedChannel()?.name}
						is_confirming={this.state.is_confirming}
						onClick={this.handleSubmit}
						is_edit={this.props.is_edit}
						is_event={false}
					/>
				</div>
			</form>
		);
	}

	render() {
		if (this.state.is_poll_creation_success) {
			let create_new_event_func: () => void;
			if (this.props.is_edit) {
				create_new_event_func = () =>
					this.props.history.push(
						common_utils.getPollCreateURL(this.props.guild_id)
					);
			} else {
				create_new_event_func = () =>
					this.setState(this.createInitialState(true));
			}

			return (
				<PollCreationConfirmation
					poll_props={this.props}
					guild_data={this.props.guild_data}
					reset_state_func={create_new_event_func}
				/>
			);
		}

		return (
			<FadeIn transitionDuration={FADE_IN_ANIMATION_DURATION}>
				<div
					className="columns is-mobile is-centered !m-0"
					style={{ transition: "opacity 0.5s", opacity: "1" }}
				>
					<div className="column is-11-mobile is-three-quarters-tablet is-three-quarters-desktop is-half-widescreen is-two-fifths-fullhd create-event-container">
						<DashboardUtils.RenderLinkChain
							link_chain_data={[
								DashboardUtils.GetGuildSelectionLinkChainData(),
								DashboardUtils.RenderGuildOptionSelectLinkChain(
									this.props.guild_data
								),
								DashboardUtils.RenderPollSelectionLinkChain(
									this.props.guild_data
								),
								DashboardUtils.RenderPollCreateLinkChain(
									this.props.guild_id,
									this.props.is_edit
								),
							]}
						/>
						{this.render_accordians()}
					</div>
				</div>
			</FadeIn>
		);
	}

	scrollToElement(id: PollCreationInputKeys) {
		document.getElementById(id).scrollIntoView({ behavior: "smooth" });
	}

	sendPollData() {
		if (
			this.props.edit_data &&
			this.props.edit_data?.end_time &&
			this.props.edit_data?.end_time < new Date()
		) {
			DashboardUtils.ShowDefaultErrorAlert(
				"This poll has already ended. Unable to edit."
			);
			this.props.history.push(
				common_utils.getGuildOptionsURL(this.props.guild_data.id)
			);
			return;
		}

		if (
			this.state.end_time &&
			this.state.end_time.toJSDate() < new Date()
		) {
			DashboardUtils.ShowDefaultErrorAlert(
				"Can not make poll with end time in the past."
			);
			return;
		}
		const selected_channel = this.getSelectedChannel();

		if (
			selected_channel.type === "Forum" &&
			selected_channel.tag_settings.required &&
			this.state.selected_channel.forum_tag_ids.length === 0
		) {
			DashboardUtils.ShowDefaultErrorAlert(
				"Must have at least one tag selected for this channel",
				undefined,
				() => this.scrollToElement("selected_channel")
			);
			return;
		}

		let request_body: IPollCreationParams = {
			user_id: this.props.user_id,
			guild_id: this.props.guild_id,
			channel_id: this.state.selected_channel.id,
			description: this.state.description,
			options: this.state.options,
			title: this.state.poll_title,
			end_time: this.state.end_time?.toISO(),
			color: this.state.color,
			poll_message_id: this.props.edit_data?.poll_message_id,
			allow_anyone_to_add: this.state.allow_anyone_to_add,
			eligible_role_ids: this.state.eligible_role_ids,
			image_url: this.state.image_url,
			single_vote_per_user: this.state.single_vote_per_user,
			is_anonymous: this.state.is_anonymous,
			access_token: this.props.auth_token.access_token,
			token_type: this.props.auth_token.token_type,
			poll_type: this.state.poll_type,
			forum_tag_ids: this.state.selected_channel.forum_tag_ids,
			role_mention_ids: this.state.role_mentions.map((val) => val.id),
			user_mentions_ids: this.state.user_mentions,
		};

		const props = this.props;

		this.setState({
			is_confirming: true,
		});

		fetch(`/api/create_poll`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(request_body),
		}).then((response) =>
			response
				.json()
				.then((val: IWebConfirmationData) => {
					if (val.error) {
						DashboardUtils.ShowDefaultErrorAlert(
							val.error.replace(/\*\*([^*]+?)\*\*/g, "<b>$1</b>")
						);
						this.setState({ is_confirming: false });
					} else {
						this.setState({
							is_poll_creation_success: true,
							is_confirming: false,
						});
					}
				})
				.catch((err) => {
					DashboardUtils.ShowDefaultErrorAlert(response.statusText);
					this.setState({ is_confirming: false });
				})
		);
	}
}

export default withRouter(PollCreation);
